import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Shield, Bell, Globe, Lock, Loader2, Save, Target, Tags, Plus, X, Trash2, GripVertical } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { toast } from 'sonner';

export default function Settings() {
  const { isAdmin } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    dahiraName: 'Daara Wakana Haqqan',
    cotisationAmount: 5000,
    annualGoal: 100000,
    currency: 'FCFA',
    notificationsEnabled: true,
    emailNotifications: true,
    publicRegistration: true,
    sassCategories: [
      { id: 'eleve', label: 'Élève', amount: 25000 },
      { id: 'etudiant', label: 'Étudiant(e)', amount: 35000 },
      { id: 'travailleur', label: 'Travailleur(se)', amount: 50000 }
    ]
  });

  const updateSassCategory = (index, field, value) => {
    setSettings(prev => {
      const cats = [...(prev.sassCategories || [])];
      cats[index] = { ...cats[index], [field]: field === 'amount' ? (parseInt(value) || 0) : value };
      return { ...prev, sassCategories: cats };
    });
  };

  const addSassCategory = () => {
    setSettings(prev => ({
      ...prev,
      sassCategories: [...(prev.sassCategories || []), { id: `cat_${Date.now()}`, label: '', amount: 0 }]
    }));
  };

  const removeSassCategory = (index) => {
    setSettings(prev => ({
      ...prev,
      sassCategories: (prev.sassCategories || []).filter((_, i) => i !== index)
    }));
  };

  // Types de contribution personnalisés
  const [contributionTypes, setContributionTypes] = useState([
    { id: 'cotisation', label: 'Cotisation', icon: '📅', color: 'from-blue-500 to-cyan-500' },
    { id: 'don', label: 'Don', icon: '💝', color: 'from-pink-500 to-rose-500' },
    { id: 'zakat', label: 'Zakat', icon: '🌙', color: 'from-emerald-500 to-green-500' },
    { id: 'sadaqa', label: 'Sadaqa', icon: '✨', color: 'from-amber-500 to-yellow-500' },
    { id: 'autre', label: 'Autre', icon: '🎁', color: 'from-purple-500 to-violet-500' }
  ]);
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [newTypeIcon, setNewTypeIcon] = useState('💰');

  const iconOptions = ['💰', '📅', '💝', '🌙', '✨', '🎁', '🕌', '📿', '🤲', '💎', '🏠', '📚', '👨‍👩‍👧‍👦', '🎓', '⚡', '🌟'];
  const colorOptions = [
    { value: 'from-blue-500 to-cyan-500', preview: 'bg-gradient-to-r from-blue-500 to-cyan-500' },
    { value: 'from-pink-500 to-rose-500', preview: 'bg-gradient-to-r from-pink-500 to-rose-500' },
    { value: 'from-emerald-500 to-green-500', preview: 'bg-gradient-to-r from-emerald-500 to-green-500' },
    { value: 'from-amber-500 to-yellow-500', preview: 'bg-gradient-to-r from-amber-500 to-yellow-500' },
    { value: 'from-purple-500 to-violet-500', preview: 'bg-gradient-to-r from-purple-500 to-violet-500' },
    { value: 'from-red-500 to-orange-500', preview: 'bg-gradient-to-r from-red-500 to-orange-500' },
    { value: 'from-indigo-500 to-blue-500', preview: 'bg-gradient-to-r from-indigo-500 to-blue-500' },
    { value: 'from-teal-500 to-emerald-500', preview: 'bg-gradient-to-r from-teal-500 to-emerald-500' },
  ];
  const [newTypeColor, setNewTypeColor] = useState(colorOptions[0].value);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Charger les paramètres généraux
        const docRef = doc(db, 'settings', 'general');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSettings(prev => ({ ...prev, ...docSnap.data() }));
        }

        // Charger les types de contribution personnalisés
        const typesRef = doc(db, 'settings', 'contribution_types');
        const typesSnap = await getDoc(typesRef);
        if (typesSnap.exists() && typesSnap.data().types?.length > 0) {
          setContributionTypes(typesSnap.data().types);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) fetchSettings();
  }, [isAdmin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Sauvegarder les paramètres généraux
      await setDoc(doc(db, 'settings', 'general'), settings);

      // Sauvegarder les types de contribution
      await setDoc(doc(db, 'settings', 'contribution_types'), { types: contributionTypes });

      toast.success('Paramètres enregistrés avec succès');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const addContributionType = () => {
    if (!newTypeLabel.trim()) {
      toast.error('Veuillez entrer un nom pour le type');
      return;
    }
    const newId = newTypeLabel.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (contributionTypes.some(t => t.id === newId)) {
      toast.error('Ce type existe déjà');
      return;
    }
    setContributionTypes([...contributionTypes, {
      id: newId,
      label: newTypeLabel.trim(),
      icon: newTypeIcon,
      color: newTypeColor
    }]);
    setNewTypeLabel('');
    setNewTypeIcon('💰');
    setNewTypeColor(colorOptions[0].value);
    toast.success('Type ajouté ! N\'oubliez pas d\'enregistrer.');
  };

  const removeContributionType = (id) => {
    if (contributionTypes.length <= 1) {
      toast.error('Vous devez garder au moins un type de contribution');
      return;
    }
    setContributionTypes(contributionTypes.filter(t => t.id !== id));
    toast.info('Type supprimé. N\'oubliez pas d\'enregistrer.');
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-semibold mb-2">Accès restreint</h2>
        <p className="text-muted-foreground">Cette page est réservée aux administrateurs</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Chargement des paramètres...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-display">Paramètres</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Configuration générale du Dahira</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl h-11 px-8 shadow-lg shadow-primary/20 transition-all active:scale-95 w-full sm:w-auto"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <Card className="border-slate-200 dark:border-slate-800 rounded-[2rem] overflow-hidden shadow-xl bg-white/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl font-bold">
              <Globe className="h-6 w-6 text-primary" />
              Paramètres généraux
            </CardTitle>
            <CardDescription>Configuration de base du Dahira</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-2">
            <div className="space-y-2">
              <Label htmlFor="dahiraName" className="text-xs font-black uppercase tracking-widest text-slate-400">Nom du Dahira</Label>
              <Input
                id="dahiraName"
                value={settings.dahiraName}
                onChange={(e) => setSettings({ ...settings, dahiraName: e.target.value })}
                className="rounded-2xl border-slate-200 h-12 focus:ring-primary/20"
                data-testid="dahira-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="cotisationAmount" className="text-xs font-black uppercase tracking-widest text-slate-400">Cotisation mensuelle</Label>
                <div className="relative">
                  <Input
                    id="cotisationAmount"
                    type="number"
                    value={settings.cotisationAmount}
                    onChange={(e) => setSettings({ ...settings, cotisationAmount: parseInt(e.target.value) })}
                    className="rounded-2xl border-slate-200 h-12 pl-4 focus:ring-primary/20"
                    data-testid="cotisation-amount-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs uppercase">{settings.currency}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="annualGoal" className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <Target className="w-3 h-3 text-emerald-500" />
                  Objectif annuel
                </Label>
                <div className="relative">
                  <Input
                    id="annualGoal"
                    type="number"
                    value={settings.annualGoal}
                    onChange={(e) => setSettings({ ...settings, annualGoal: parseInt(e.target.value) })}
                    className="rounded-2xl border-slate-200 h-12 pl-4 focus:ring-primary/20"
                    data-testid="annual-goal-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs uppercase">{settings.currency}</span>
                </div>
                <p className="text-[10px] text-slate-400 italic">C'est le montant cible pour chaque membre par an.</p>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="currency" className="text-xs font-black uppercase tracking-widest text-slate-400">Devise</Label>
                <Input
                  id="currency"
                  value={settings.currency}
                  onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                  className="rounded-2xl border-slate-200 h-12 focus:ring-primary/20"
                  data-testid="currency-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="border-slate-200 dark:border-slate-800 rounded-[2rem] overflow-hidden shadow-xl bg-white/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl font-bold">
              <Bell className="h-6 w-6 text-primary" />
              Notifications
            </CardTitle>
            <CardDescription>Gérez les préférences de notification</CardDescription>
          </CardHeader>
          <CardContent className="space-y-0 pt-2">
            <div className="flex items-center justify-between py-6">
              <div className="space-y-1">
                <p className="font-bold text-slate-900 dark:text-slate-100">Notifications activées</p>
                <p className="text-sm text-slate-500">Activer les notifications système pour les membres</p>
              </div>
              <Switch
                checked={settings.notificationsEnabled}
                onCheckedChange={(checked) => setSettings({ ...settings, notificationsEnabled: checked })}
                className="data-[state=checked]:bg-primary"
                data-testid="notifications-switch"
              />
            </div>

            <Separator className="bg-slate-100" />

            <div className="flex items-center justify-between py-6">
              <div className="space-y-1">
                <p className="font-bold text-slate-900 dark:text-slate-100">Notifications par email</p>
                <p className="text-sm text-slate-500">Envoyer automatiquement des emails aux membres</p>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={(checked) => setSettings({ ...settings, emailNotifications: checked })}
                className="data-[state=checked]:bg-primary"
                data-testid="email-notifications-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Catégories Sass (objectif annuel par profil) */}
        <Card className="border-slate-200 dark:border-slate-800 lg:col-span-2 rounded-[2rem] overflow-hidden shadow-xl bg-white/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl font-bold">
              <Target className="h-6 w-6 text-emerald-500" />
              Catégories Sass
            </CardTitle>
            <CardDescription>Définissez l'objectif annuel (Sass) selon le profil du membre. Ces catégories sont assignables depuis la page Membres.</CardDescription>
          </CardHeader>
          <CardContent className="pt-2 space-y-4">
            <div className="space-y-3">
              {(settings.sassCategories || []).map((cat, index) => (
                <div key={cat.id || index} className="flex items-end gap-3 p-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Catégorie</Label>
                    <Input
                      value={cat.label}
                      onChange={(e) => updateSassCategory(index, 'label', e.target.value)}
                      placeholder="Ex: Élève"
                      className="rounded-xl h-11"
                    />
                  </div>
                  <div className="w-40 space-y-1">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Montant / an</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={cat.amount}
                        onChange={(e) => updateSassCategory(index, 'amount', e.target.value)}
                        className="rounded-xl h-11 pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-[10px] uppercase">{settings.currency}</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeSassCategory(index)}
                    className="h-11 w-11 rounded-xl text-red-500 hover:bg-red-50 hover:text-red-600 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addSassCategory}
              className="rounded-2xl h-11"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une catégorie
            </Button>
            <p className="text-[11px] text-slate-400 italic">N'oubliez pas d'enregistrer après modification.</p>
          </CardContent>
        </Card>

        {/* Types de Contribution Personnalisés */}
        <Card className="border-slate-200 dark:border-slate-800 lg:col-span-2 rounded-[2rem] overflow-hidden shadow-xl bg-white/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl font-bold">
              <Tags className="h-6 w-6 text-primary" />
              Types de contribution
            </CardTitle>
            <CardDescription>Personnalisez les types de contribution disponibles pour les membres</CardDescription>
          </CardHeader>
          <CardContent className="pt-2 space-y-6">
            {/* Liste des types existants */}
            <div className="space-y-3">
              <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Types actuels</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {contributionTypes.map((type) => (
                  <div
                    key={type.id}
                    className="relative group p-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-all"
                  >
                    <button
                      type="button"
                      onClick={() => removeContributionType(type.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-3 shadow-lg`}>
                      <span className="text-2xl">{type.icon}</span>
                    </div>
                    <p className="font-bold text-slate-900 dark:text-white">{type.label}</p>
                    <p className="text-[10px] text-slate-400 font-mono">{type.id}</p>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Ajouter un nouveau type */}
            <div className="space-y-4">
              <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Ajouter un nouveau type</Label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Nom du type</Label>
                  <Input
                    value={newTypeLabel}
                    onChange={(e) => setNewTypeLabel(e.target.value)}
                    placeholder="Ex: Projet Mosquée"
                    className="rounded-xl h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Icône</Label>
                  <div className="flex flex-wrap gap-1.5 p-2 rounded-xl border border-slate-200 bg-slate-50 max-h-24 overflow-auto">
                    {iconOptions.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setNewTypeIcon(icon)}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${newTypeIcon === icon
                            ? 'bg-primary text-white shadow-lg scale-110'
                            : 'hover:bg-slate-200'
                          }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Couleur</Label>
                  <div className="flex flex-wrap gap-1.5 p-2 rounded-xl border border-slate-200 bg-slate-50">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setNewTypeColor(color.value)}
                        className={`w-9 h-9 rounded-lg ${color.preview} transition-all ${newTypeColor === color.value
                            ? 'ring-2 ring-offset-2 ring-primary scale-110'
                            : ''
                          }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${newTypeColor} flex items-center justify-center shadow-lg`}>
                  <span className="text-2xl">{newTypeIcon}</span>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-900 dark:text-white">{newTypeLabel || 'Nom du type'}</p>
                  <p className="text-xs text-slate-400">Aperçu de votre nouveau type</p>
                </div>
                <Button
                  type="button"
                  onClick={addContributionType}
                  className="rounded-2xl h-11 px-6 bg-primary hover:bg-primary/90"
                  disabled={!newTypeLabel.trim()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security / Access */}
        <Card className="border-slate-200 dark:border-slate-800 lg:col-span-2 rounded-[2rem] overflow-hidden shadow-xl bg-white/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl font-bold">
              <Lock className="h-6 w-6 text-primary" />
              Sécurité et Accès
            </CardTitle>
            <CardDescription>Configurez les règles d'adhésion au Dahira</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="flex items-center justify-between py-6">
              <div className="space-y-1">
                <p className="font-bold text-slate-900 dark:text-slate-100">Inscription publique</p>
                <p className="text-sm text-slate-500">
                  Permettre aux nouveaux membres de créer leur compte eux-mêmes via la page d'accueil
                </p>
              </div>
              <Switch
                checked={settings.publicRegistration}
                onCheckedChange={(checked) => setSettings({ ...settings, publicRegistration: checked })}
                className="data-[state=checked]:bg-primary"
                data-testid="public-registration-switch"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
